import type { CsvImportStatus } from '@/lib/csv-import/api';
import { ApiError, isTransientError } from '@/lib/errors';
import { apiGet, apiPost } from '@/lib/http/client';

// Poll cadence for a dispatched wizard job. 1.5s is responsive enough that a
// small org's import still feels immediate, without hammering the endpoint
// through the minutes a real org's fetch can take.
const JOB_POLL_INTERVAL_MS = 1500;
const JOB_POLL_TIMEOUT_MS = 35 * 60 * 1000;
// Transient network errors (5xx, dropped connections) retry up to this many
// times before the error propagates — avoids killing a 30-minute import on a
// brief connectivity hiccup.
const MAX_NETWORK_RETRIES = 3;

export interface ValidateKeyResponse {
  valid: boolean;
}

export interface DiscoveredObjectType {
  label: string;
  entity_type_name: string;
}

export interface DiscoverTypesResponse {
  custom_object_types: DiscoveredObjectType[];
  /**
   * Which standard objects the workspace actually has, keyed by Ahoy entity
   * type name (ahoy_contact/ahoy_company/ahoy_deal). Attio workspaces enable
   * some standard objects optionally (deals in particular); the Configure step
   * disables absent ones so the user can't select an object whose walk would
   * fail. Optional for backward compatibility with older backends.
   */
  standard_object_types?: Record<string, boolean>;
}

/**
 * Both heavy wizard steps are dispatched, not run inline: they walk every record
 * at Attio's 25-rows-per-page cap, which used to run inside one request
 * against a 30s gunicorn timeout and completed only for demo-sized orgs. The
 * POST returns 202 with a job id; the client polls until terminal.
 */
export interface StartJobResponse {
  job_id: number;
}

export type AttioJobStatus = 'pending' | 'running' | 'succeeded' | 'failed';

export type AttioJobErrorCode =
  | 'attio_unreachable'
  | 'invalid_key'
  | 'no_importable_types'
  | 'invalid_mappings'
  | 'pipeline_contended'
  | 'internal';

export interface AttioJob {
  id: number;
  kind: 'proposals' | 'create';
  status: AttioJobStatus;
  /**
   * Kind-specific success payload — `MappingProposalsResult` for a proposals
   * job, `CreateImportResult` for a create job. Null until the job succeeds.
   */
  result: Record<string, unknown> | null;
  error_code: AttioJobErrorCode | '';
  error_detail: Record<string, unknown> | null;
  /** Set by a successful create job; the client hands off to the CSV import poll. */
  csv_import_id: number | null;
  created_at: string;
  finished_at: string | null;
}

export interface CreateImportResult {
  status: CsvImportStatus;
  /**
   * Present only on partial success: the import was created but the association
   * types for selected custom objects could not be materialized, so those
   * relationships will be missing until the import is retried. The job still
   * succeeds — the records import regardless.
   */
  warning?: string;
}

export type AttioMappingAction = 'map_existing' | 'create_new' | 'skip';

export interface AttioMappingProposal {
  raw_key: string;
  label: string;
  entity_type_name: string;
  proposed_action: AttioMappingAction;
  target_property_name: string;
  field_type_name: string;
  /**
   * Multi-valued shape of the reviewed field (MULTI_SELECT). Echoed back on
   * confirmation so the backend re-check can reject a map_existing target whose
   * authoritative is_multi has drifted. Optional for backward compatibility.
   */
  is_multi?: boolean;
  /**
   * How many scanned records carry a value this field would ACTUALLY import,
   * counted with the same predicate the importer applies — so it never promises
   * values that get dropped. A zero-count field is proposed as `skip`, and
   * proposals arrive most-populated-first.
   */
  populated_count?: number;
  /**
   * Whether the engine resolved a compatible EXISTING property for this field.
   *
   * Read this rather than inferring from `proposed_action` or
   * `target_property_name`: a zero-count field is proposed as `skip` whichever it
   * was, and `target_property_name` holds the collision-free NEW name on the
   * create branch — so both are ambiguous exactly when it matters.
   */
  matched_existing?: boolean;
}

export interface MappingProposalsResult {
  proposals: Record<string, AttioMappingProposal[]>;
  /**
   * Per-entity-type flag: true when the backend capped this entity type's
   * proposals at MAX_PROPOSALS_PER_ENTITY_TYPE (500) and dropped the rest.
   * A truncated entity type's review list is incomplete even though the
   * request succeeded. The dropped tail is the least-populated end of the list.
   */
  truncated: Record<string, boolean>;
  /**
   * Records scanned per entity type — the denominator for `populated_count`
   * ("3 of 285 contacts have this set").
   */
  total_records: Record<string, number>;
  /**
   * Standard types the backend's workspace guard dropped (entity-type names) —
   * normally empty, since the Configure step already prevents selecting an
   * object the workspace doesn't have. Optional for backward compatibility.
   */
  dropped_object_types?: string[];
}

/** Shape of one entry in a create-import 400's `invalid_mappings` detail. */
export interface InvalidMappingDetail {
  entity_type_name: string;
  raw_key: string;
  target_property_name: string;
}

export interface AttioConfirmedMapping {
  raw_key: string;
  entity_type_name: string;
  action: AttioMappingAction;
  target_property_name: string;
  field_type_name: string;
  is_multi: boolean;
}

export interface CreateAttioImportParams {
  apiKey: string;
  selectedObjectTypes: string[];
  name?: string;
  confirmedMappings?: AttioConfirmedMapping[];
}

export async function validateAttioKey(
  orgId: number,
  apiKey: string
): Promise<ValidateKeyResponse> {
  return apiPost<ValidateKeyResponse>(
    `/api/orgs/${orgId}/attio-import/validate-key/`,
    { api_key: apiKey }
  );
}

export async function discoverAttioTypes(
  orgId: number,
  apiKey: string
): Promise<DiscoverTypesResponse> {
  return apiPost<DiscoverTypesResponse>(
    `/api/orgs/${orgId}/attio-import/discover-object-types/`,
    { api_key: apiKey }
  );
}

export async function startAttioMappingProposals(
  orgId: number,
  params: { apiKey: string; selectedObjectTypes: string[] },
  signal?: AbortSignal
): Promise<StartJobResponse> {
  return apiPost<StartJobResponse>(
    `/api/orgs/${orgId}/attio-import/mapping-proposals/`,
    {
      api_key: params.apiKey,
      selected_object_types: params.selectedObjectTypes,
    },
    undefined,
    { signal }
  );
}

export async function startAttioImport(
  orgId: number,
  params: CreateAttioImportParams,
  signal?: AbortSignal
): Promise<StartJobResponse> {
  return apiPost<StartJobResponse>(
    `/api/orgs/${orgId}/attio-import/imports/`,
    {
      api_key: params.apiKey,
      selected_object_types: params.selectedObjectTypes,
      ...(params.name ? { name: params.name } : {}),
      ...(params.confirmedMappings
        ? { confirmed_mappings: params.confirmedMappings }
        : {}),
    },
    undefined,
    { signal }
  );
}

export async function getAttioJob(
  orgId: number,
  jobId: number,
  signal?: AbortSignal
): Promise<AttioJob> {
  return apiGet<AttioJob>(
    `/api/orgs/${orgId}/attio-import/jobs/${jobId}/`,
    undefined,
    undefined,
    signal
  );
}

/** Thrown when a dispatched job reaches a terminal `failed` state. */
export class AttioJobFailedError extends Error {
  readonly code: AttioJobErrorCode | '';
  readonly detail: Record<string, unknown> | null;

  constructor(job: AttioJob) {
    super(`Attio job ${job.id} failed: ${job.error_code || 'unknown'}`);
    this.name = 'AttioJobFailedError';
    this.code = job.error_code;
    this.detail = job.error_detail;
  }
}

/** Thrown when polling gives up. The job itself may still be running. */
export class AttioJobTimeoutError extends Error {
  constructor() {
    super('Timed out waiting for the Attio job');
    this.name = 'AttioJobTimeoutError';
  }
}

/**
 * Poll a dispatched job until it reaches a terminal state.
 *
 * The ceiling is deliberately longer than the backend tasks' own hard time
 * limits (16 min for proposals, 31 min for create) so this never gives up on a
 * job that is still legitimately working — the whole point of moving this work
 * off the request path was that a real org's import does not fit in 30 seconds.
 * A timeout here means "we stopped watching", not "it failed", and the copy
 * says so.
 *
 * Accepts an optional AbortSignal to cancel polling (e.g. on wizard unmount).
 * Transient network errors are retried up to MAX_NETWORK_RETRIES times before
 * propagating — a brief connectivity hiccup will not kill a long-running import.
 */
export async function waitForAttioJob(
  orgId: number,
  jobId: number,
  signal?: AbortSignal
): Promise<AttioJob> {
  const deadline = Date.now() + JOB_POLL_TIMEOUT_MS;
  let networkErrorCount = 0;
  for (;;) {
    if (signal?.aborted) {
      throw new DOMException('Polling aborted', 'AbortError');
    }
    let job: AttioJob;
    try {
      job = await getAttioJob(orgId, jobId, signal);
      networkErrorCount = 0;
    } catch (err) {
      if (err instanceof AttioJobFailedError) {
        throw err;
      }
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      // Only transient failures (5xx, dropped connections, timeouts) are worth
      // retrying. A non-transient status like 400/404 is a definitive answer —
      // retrying it just burns MAX_NETWORK_RETRIES * JOB_POLL_INTERVAL_MS (~4.5s)
      // of redundant polling before surfacing the same error to the user.
      if (!isTransientError(err) || networkErrorCount >= MAX_NETWORK_RETRIES) {
        throw err;
      }
      networkErrorCount++;
      if (signal?.aborted) {
        throw new DOMException('Polling aborted', 'AbortError');
      }
      // Honor the polling ceiling even when the latest attempt failed
      // transiently. Without this, a hiccup at/after the deadline would keep
      // retrying and ultimately surface the raw network error instead of the
      // "still running" timeout — the same contract the success path enforces
      // below.
      if (Date.now() >= deadline) {
        throw new AttioJobTimeoutError();
      }
      await new Promise(resolve => setTimeout(resolve, JOB_POLL_INTERVAL_MS));
      continue;
    }
    if (job.status === 'succeeded') {
      return job;
    }
    if (job.status === 'failed') {
      throw new AttioJobFailedError(job);
    }
    if (Date.now() >= deadline) {
      throw new AttioJobTimeoutError();
    }
    if (signal?.aborted) {
      throw new DOMException('Polling aborted', 'AbortError');
    }
    await new Promise(resolve => setTimeout(resolve, JOB_POLL_INTERVAL_MS));
  }
}

export const JOB_ERROR_COPY: Record<AttioJobErrorCode, string> = {
  attio_unreachable:
    "We couldn't reach Attio. Check your connection and try again.",
  invalid_key: 'That API key is invalid or expired.',
  no_importable_types:
    "Custom objects aren't available on this plan, and no other object types were selected.",
  invalid_mappings:
    'Some mappings are no longer valid. Review them and try again.',
  pipeline_contended:
    'Another import is still wrapping up. Give it a moment, then try again.',
  internal:
    'Something went wrong. Try again, and get in touch if it keeps happening.',
};

export function describeAttioError(err: unknown): string {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return 'Import cancelled.';
  }
  // A dispatched job reports its failure in the poll response, not as an HTTP
  // status, so this case comes first — an ApiError here would only ever be a
  // failure of the poll request itself.
  if (err instanceof AttioJobFailedError) {
    return (
      JOB_ERROR_COPY[err.code as AttioJobErrorCode] ?? JOB_ERROR_COPY.internal
    );
  }
  if (err instanceof AttioJobTimeoutError) {
    return "This is taking longer than expected. It's still running – check the imports list in a few minutes.";
  }
  if (err instanceof ApiError) {
    switch (err.statusCode) {
      case 502:
        return "We couldn't reach Attio. Check your connection and try again.";
      case 400:
        // A 400 can be a rejected key OR a bad request body (e.g. invalid
        // selected object types), so stay neutral instead of asserting the key
        // was the problem and pointing the user at the wrong fix.
        return "We couldn't complete that request. Double-check your API key and selections, then try again.";
      case 404:
        return "Attio import isn't available for this workspace.";
      default:
        return 'Something went wrong. Try again, and get in touch if it keeps happening.';
    }
  }
  return 'Something went wrong. Try again, and get in touch if it keeps happening.';
}

function extractInvalidMappings(response: unknown): InvalidMappingDetail[] {
  if (
    !response ||
    typeof response !== 'object' ||
    !Array.isArray((response as Record<string, unknown>).invalid_mappings)
  ) {
    return [];
  }
  return (
    (response as Record<string, unknown>).invalid_mappings as unknown[]
  ).filter((entry): entry is InvalidMappingDetail => {
    if (typeof entry !== 'object' || entry === null) {
      return false;
    }
    const candidate = entry as Record<string, unknown>;
    return (
      typeof candidate.entity_type_name === 'string' &&
      typeof candidate.raw_key === 'string' &&
      typeof candidate.target_property_name === 'string'
    );
  });
}

/**
 * Describes a create-import failure. When the backend's 400 carries an
 * `invalid_mappings` detail (a confirmed map_existing target that no longer
 * exists by submit time), names the offending row(s) so the user knows what
 * to fix. Falls back to describeAttioError's generic copy otherwise.
 */
export function describeConfirmError(err: unknown): string {
  // The named-rows detail now rides on the failed job's error_detail rather than
  // a 400 body, since the create call returns 202 before the work starts.
  if (err instanceof AttioJobFailedError && err.code === 'invalid_mappings') {
    const invalidMappings = extractInvalidMappings(err.detail);
    if (invalidMappings.length > 0) {
      const rows = invalidMappings
        .map(m => `"${m.raw_key}" (${m.entity_type_name})`)
        .join(', ');
      return `These mappings are no longer valid: ${rows}. Review them and try again.`;
    }
  }
  if (err instanceof ApiError && err.statusCode === 400) {
    const invalidMappings = extractInvalidMappings(err.response);
    if (invalidMappings.length > 0) {
      const rows = invalidMappings
        .map(m => `"${m.raw_key}" (${m.entity_type_name})`)
        .join(', ');
      return `These mappings are no longer valid: ${rows}. Review them and try again.`;
    }
  }
  return describeAttioError(err);
}
